import { filtersToDbQuery } from '@shared/shared.utils'
import {
  SetDefaultData,
  FetchBusinessRequest,
  UpdateField,
  UpdateBusinessRequest,
  FetchPropertyBuildingRequest,
  ArchiveBusinessRequest,
} from '../../businesses.actions'
import { getUpdateBusinessState } from '../../businesses.selectors'
import Validator from '@shared/validatorjs/src/validator'
import { ActivatedRoute } from '@angular/router'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy, Input } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Map, fromJS, isImmutable, isList } from 'immutable'
import {
  BehaviorSubject,
  Subject,
  Subscription,
  combineLatest,
  debounceTime,
  filter,
} from 'rxjs'
import { FetchPropertiesRequest } from '@containers/properties/properties.actions'
import { getPropertiesState } from '@containers/properties/properties.selectors'
import {
  DetectAllLayersRequest,
  DetectLayerRequest,
  FetchActiveLayersRequest,
  FetchPropertyTypesRequest,
  SetDefaultData as SetSharedDefaultData,
} from '@shared/shared.actions'
import {
  FetchBusinessCategoriesRequest,
  FetchContactTitlesRequest,
  FetchContactsRequest,
  FetchOrganizationsRequest,
} from '@shared/shared.actions'
import {
  getActiveLayers,
  getBusinessCategoriesState,
  getContactTitlesState,
  getContactsState,
  getDetectedLayer,
  getDetectedLayers,
  getOrganizationsState,
  getPropertyTypesState,
} from '@shared/shared.select'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { GeoJSON } from 'ol/format'
import { Feature } from 'ol'
import { Point } from 'ol/geom'
import { transform } from 'ol/proj'
import { getSettingsState } from '@containers/settings/settings.selectors'

@Component({
  selector: 'general',
  templateUrl: './general.component.html',
  styleUrls: ['./general.component.css'],
})
export class GeneralComponent extends FormTrait implements OnInit, OnDestroy {
  @Input() isReadonly: boolean = false
  public isLoading: boolean
  public validationRequired: boolean = false
  public businessId: number
  private state$: Subscription
  public building: any
  public propertyEditable: boolean = false
  public propertyAutocomplete$: Subscription
  public onPropertyAutocomplete$ = new Subject<string>()
  public properties$: Subscription
  public properties = []
  public registrantsAutocomplete$: Subscription
  public onRegistrantsAutocomplete$ = new Subject<string>()
  public registrants$: Subscription
  public registrants = []
  public contacts$: Subscription
  public contacts = []
  public contactsOptions = []
  public categories = []
  public categories$: Subscription
  public categoryAutocomplete$: Subscription
  public onCategoryAutocomplete$ = new Subject<string>()
  public contactAutocomplete$: Subscription
  public onContactAutocomplete$ = new Subject<string>()
  public contactTitles$: Subscription
  public contactTitles = []
  public contactTitlesAutocomplete$: Subscription
  public onContactTitlesAutocomplete$ = new Subject<string>()
  public separatorKeys = [',', ' ']
  public radioOptions = [
    {
      label: 'Area',
      value: false,
    },
    {
      label: 'Floors',
      value: true,
    },
  ]
  public entityOptions = [
    {
      label: 'Property',
      value: 1,
    },
    {
      label: 'Location',
      value: 2,
    },
  ]
  public registrantsValue = []
  public subcategories = []
  public logicSlots = []
  public logicSlotsOptions = []
  public showLogicSlots = false
  public propertiesOptions = []
  public contactConnectionValue: any
  public entityType = 1

  public layers: FormGroup
  private detectLayerState$: Subscription
  private detectAllLayersState$: Subscription
  private activeLayersState$: Subscription
  private dataReady$: Subscription

  private businessLayerFeaturesReady = new BehaviorSubject<boolean>(false)
  private layersReady = new BehaviorSubject<boolean>(false)
  public detailsLoading: boolean
  public filteredCategories: any[] = []
  public filteredSubCategories: any[] = []
  public selectedContactId: any
  public isInitial = true
  private minLat: number
  private maxLat: number
  private minLng: number
  private maxLng: number

  private settings$: Subscription
  public isInitialEntitySet = false
  public areaUnit: string
  public selectedPropertyId: any

  public propertyTypes$: Subscription
  public propertyTypes = []

  constructor(
    public store: Store<any>,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
  ) {
    super()
  }

  ngOnInit() {
    this.store.dispatch(new SetDefaultData('update'))
    this.clearSubcategoriesAndSlots()
    this.businessId = parseInt(this.route.snapshot.paramMap.get('id'))

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        const settings = state.get('data')
        const gisExtent = settings.getIn(['gis', 'GIS_MAP_EXTENT'])
        this.areaUnit = settings.getIn(['units', 'SETTINGS_UNITS_AREA_SYMBOL'])

        if (gisExtent) {
          const coordinates = gisExtent
            .split(',')
            .map((c: string) => parseFloat(c))

          this.minLat = coordinates[1]
          this.maxLat = coordinates[3]
          this.minLng = coordinates[0]
          this.maxLng = coordinates[2]
        }
      })

    this.layers = this.formBuilder.group({
      rows: this.formBuilder.array([]),
    })

    this.layers.valueChanges.subscribe(() => {
      const businessLayerFeatures = this.activeLayers
        .map((row) => ({
          layerId: row.get('id').value,
          id: row.get('feature').value,
        }))
        .filter(({ id }) => !!id)

      this.store.dispatch(
        new UpdateField(
          'businessLayerFeatures',
          businessLayerFeatures,
          `update.data`,
        ),
      )
    })

    if (this.state && this.state.getIn(['data', 'id']) !== this.businessId) {
      this.store.dispatch<any>(new SetDefaultData('update'))
      this.store.dispatch<any>(new SetSharedDefaultData('contacts'))
      this.store.dispatch<any>(new SetSharedDefaultData('contactTitles'))
    }

    this.dataReady$ = combineLatest([
      this.businessLayerFeaturesReady.asObservable(),
      this.layersReady.asObservable(),
    ])
      .pipe(
        filter(
          ([businessLayerFeatures, layers]) => businessLayerFeatures && layers,
        ),
      )
      .subscribe(() => {
        const data = this.state.get('data').toJS()
        data.businessLayerFeatures.forEach((row: any) => {
          if (row.layerFeature) {
            setTimeout(() => {
              this.selectedDetectedLayer(
                row.layerFeature.id,
                row.layerFeature.layerId,
              )
            }, 500)
          }
        })
      })

    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        const rows = this.layers.get('rows') as FormArray
        rows.clear()
        const data = state.toJS()

        data
          .filter(({ delivery }) => !!delivery)
          .forEach((layer: any) => {
            rows.push(
              this.formBuilder.group({
                name: [layer.name],
                id: [layer.id],
                features: [layer.features],
                feature: [null],
              }),
            )
          })

        if (rows.length) {
          this.layersReady.next(true)
        }
      })

    this.detectLayerState$ = this.store
      .pipe(select(getDetectedLayer))
      .subscribe((state) => {
        if (!state.get('isLoading') && state.getIn(['data', 'layerId'])) {
          this.selectedDetectedLayer(
            state.getIn(['data', 'id']),
            state.getIn(['data', 'layerId']),
          )
        }
      })

    this.detectAllLayersState$ = this.store
      .pipe(select(getDetectedLayers))
      .subscribe((state) => {
        if (!state.get('isLoading')) {
          state
            .get('data')
            .toJS()
            .forEach((layer: any) => {
              this.selectedDetectedLayer(layer.featureId, layer.layerId)
            })
        }
      })

    this.state$ = this.store
      .pipe(select(getUpdateBusinessState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
        this.detailsLoading = state.get('detailsLoading')
        this.validationRequired = !!state.getIn(['data', 'checks', 'validationRequired'])
        this.building = state.get('building')?.toJS()
        const data = this.state.get('data').toJS()
        const businessId = this.state.getIn(['data', 'id'])
        const propertyId = this.state.getIn([
          'data',
          'propertyConnection',
          'propertyId',
        ])
        const propertyType = this.state.getIn([
          'data',
          'propertyConnection',
          'propertyType',
        ])
        const categoryPropertyType = this.state.getIn([
          'data',
          'subcategoryConnection',
          'subCategory',
          'propertyType',
        ]) as string

        if (
          propertyId &&
          !propertyType &&
          categoryPropertyType &&
          this.isPropertyTypeInList(categoryPropertyType)
        ) {
          this.store.dispatch(
            new UpdateField(
              'propertyType',
              categoryPropertyType,
              `update.data.propertyConnection`,
            ),
          )
        }

        if (!!propertyId && this.selectedPropertyId !== propertyId) {
          this.store.dispatch<any>(new FetchPropertyTypesRequest(propertyId))

          this.selectedPropertyId = propertyId
        }

        if (data.businessLayerFeatures && data.businessLayerFeatures.length) {
          this.businessLayerFeaturesReady.next(true)
        }

        const contactId = state.getIn([
          'data',
          'contactConnection',
          'contact',
          'id',
        ])

        if (!!businessId && !this.selectedContactId) {
          if (!!contactId && this.selectedContactId != contactId) {
            this.selectedContactId = contactId

            this.store.dispatch<any>(
              new FetchContactsRequest({
                filters: {
                  matchOr: [
                    {
                      field: 'id',
                      value: this.selectedContactId,
                    },
                  ],
                },
                pageSize: 1,
              }),
            )
          } else {
            if (this.contacts.length == 0) {
              this.store.dispatch<any>(
                new FetchContactsRequest({
                  filters: {},
                  pageSize: 50,
                }),
              )
            }
          }
        }

        this.registrantsValue = state.getIn(['data', 'registrants'])
          ? state
              .getIn(['data', 'registrants'])
              .toJS()
              .map(({ id }) => id)
          : []

        if (
          businessId &&
          !this.state.getIn(['data', 'propertyConnection', 'propertyCode']) &&
          !this.isInitialEntitySet
        ) {
          this.isInitialEntitySet = true
          this.entityType = 2
        }

        if (!this.properties.length && !!businessId) {
          const propertyCode = this.state.getIn([
            'data',
            'propertyConnection',
            'propertyCode',
          ])
          this.store.dispatch<any>(
            new FetchPropertiesRequest(
              null,
              propertyCode
                ? {
                    filters: {
                      or: [
                        { field: 'propertyCodeAssigned', value: propertyCode },
                      ],
                    },
                    pageSize: 5,
                  }
                : {},
            ),
          )
        }

        const licenseTypeId = state.getIn(['data', 'licenseType', 'id'])

        if (!this.categories.length && licenseTypeId) {
          this.store.dispatch<any>(
            new FetchBusinessCategoriesRequest({
              filters: {
                matchOr: [{ field: 'licenseType.id', value: licenseTypeId }],
              },
              pageSize: 50,
            }),
          )
        }

        this.fillSubcategoriesAndLogicSlots()

        this.filterCategoriesAndSubCategories()
      })

    this.properties$ = this.store
      .pipe(select(getPropertiesState))
      .subscribe((state) => {
        this.properties = state.get('data').toJS()
        this.propertiesOptions = this.properties.map(
          ({ propertyCodeAssigned, id }) => ({
            name: propertyCodeAssigned,
            id: id,
          }),
        )
      })

    this.registrants$ = this.store
      .pipe(select(getOrganizationsState))
      .subscribe((state) => {
        this.registrants = state.get('data').toJS()
      })

    this.categories$ = this.store
      .pipe(select(getBusinessCategoriesState))
      .subscribe((state) => {
        this.categories = state.get('data').toJS()

        this.fillSubcategoriesAndLogicSlots()

        this.filterCategoriesAndSubCategories()
      })

    this.contacts$ = this.store
      .pipe(select(getContactsState))
      .subscribe((state) => {
        this.contacts = state.get('data').toJS()
        if (this.contacts.length) {
          this.contactConnectionValue = this.getField([
            'contactConnection',
            'contact',
          ])
        }
      })

    this.contactTitles$ = this.store
      .pipe(select(getContactTitlesState))
      .subscribe((state) => {
        this.contactTitles = state.get('data').toJS()
      })

    this.propertyTypes$ = this.store
      .pipe(select(getPropertyTypesState))
      .subscribe((state) => {
        this.propertyTypes = state.toJS()
      })

    this.propertyAutocomplete$ = this.onPropertyAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        if (keyword !== null) {
          this.store.dispatch<any>(
            new FetchPropertiesRequest(null, {
              filters: {
                or: [{ field: 'propertyCodeAssigned', value: keyword }],
              },
              pageSize: 50,
            }),
          )
        }
      })

    this.registrantsAutocomplete$ = this.onRegistrantsAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchOrganizationsRequest({
            filters: {
              or: [{ field: 'name', value: keyword ?? '' }],
            },
            pageSize: 50,
          }),
        )
      })

    this.categoryAutocomplete$ = this.onCategoryAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        const licenseTypeId = this.state.getIn(['data', 'licenseType', 'id'])

        this.store.dispatch<any>(
          new FetchBusinessCategoriesRequest({
            filters: {
              or: [{ field: 'name', value: keyword ?? '' }],
              matchOr: [{ field: 'licenseType.id', value: licenseTypeId }],
            },
            pageSize: 50,
          }),
        )
      })

    this.contactAutocomplete$ = this.onContactAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword = '') => {
        this.store.dispatch<any>(
          new FetchContactsRequest({
            filters: {
              or: [
                { field: 'fullName', value: keyword ?? '' },
                { field: 'taxId', value: keyword ?? '' },
              ],
            },
            pageSize: 50,
          }),
        )
      })

    this.contactTitlesAutocomplete$ = this.onContactTitlesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword = '') => {
        this.store.dispatch<any>(
          new FetchContactTitlesRequest({
            filters: {
              or: [{ field: 'name', value: keyword ?? '' }],
            },
            pageSize: 50,
          }),
        )
      })

    this.store.dispatch<any>(
      new FetchOrganizationsRequest({
        pageSize: 50,
      }),
    )

    this.store.dispatch<any>(
      new FetchContactTitlesRequest({
        pageSize: 50,
      }),
    )

    if (this.isInitial) {
      this.isInitial = false
    }

    this.store.dispatch<any>(new FetchActiveLayersRequest())
  }

  clearSubcategoriesAndSlots() {
    this.subcategories = []
    this.filteredSubCategories = []
    this.logicSlotsOptions = []
    this.showLogicSlots = false
  }

  fillSubcategoriesAndLogicSlots() {
    const categoryId = this.state.getIn([
      'data',
      'subcategoryConnection',
      'subCategory',
      'categoryId',
    ])
    if (categoryId) {
      const category = this.categories.find(({ id }) => id == categoryId)

      if (category) {
        const hasProperty = this.state.getIn(['data', 'propertyConnection'])
        this.subcategories = category.subCategories.filter((subCat: any) => {
          const allowedSlotTypes = subCat?.subCategoryType?.allowedSlotTypes
          if (!hasProperty) {
            return allowedSlotTypes.includes('FIXED_MANUAL')
          }

          return (
            allowedSlotTypes.includes('FIXED_RANGE') ||
            allowedSlotTypes.includes('PROGRESSIVE')
          )
        })

        const subCategory = (
          this.state.getIn([
            'data',
            'subcategoryConnection',
            'subCategory',
          ]) as any
        ).toJS()

        if (subCategory && subCategory.categoryId) {
          const targetSubCategory = this.subcategories.find(
            ({ id }) => id === subCategory.id,
          )

          if (targetSubCategory) {
            this.logicSlots = targetSubCategory.logicSlots

            const allowedSlotTypes =
              targetSubCategory?.subCategoryType?.allowedSlotTypes

            this.logicSlotsOptions = this.logicSlots
              .filter((slot: any) => {
                return allowedSlotTypes.includes(slot.calculationType.name)
              })
              .map(({ id, name }) => ({ label: name, value: id }))

            this.showLogicSlots =
              allowedSlotTypes?.includes('FIXED_MANUAL') &&
              !this.state.getIn(['data', 'propertyConnection'])
          } else {
            this.logicSlotsOptions = []
            this.showLogicSlots = false
          }
        } else {
          this.clearSubcategoriesAndSlots()
        }
      }
    }
  }

  filterCategoriesAndSubCategories() {
    this.filteredCategories = this.categories.filter((category: any) => {
      if (this.getField(['propertyConnection'])) {
        return category.forProperty
      } else {
        return category.forNoProperty
      }
    })

    this.filteredSubCategories = this.subcategories.filter((category: any) => {
      if (this.getField(['propertyConnection'])) {
        return category.forProperty
      } else {
        return category.forNoProperty
      }
    })
  }

  get hasCoordinates() {
    return this.getField(['lng']) && this.getField(['lat'])
  }

  ngOnDestroy() {
    this.clearSubcategoriesAndSlots()
    this.state$.unsubscribe()
    this.propertyAutocomplete$.unsubscribe()
    this.registrantsAutocomplete$.unsubscribe()
    this.categoryAutocomplete$.unsubscribe()
    this.contactAutocomplete$.unsubscribe()
    this.properties$.unsubscribe()
    this.registrants$.unsubscribe()
    this.contacts$.unsubscribe()
    this.categories$.unsubscribe()
    this.contactTitles$.unsubscribe()
    this.contactTitlesAutocomplete$.unsubscribe()
    this.dataReady$.unsubscribe()
    this.settings$.unsubscribe()
    this.propertyTypes$.unsubscribe()

    if (this.activeLayersState$) {
      this.activeLayersState$.unsubscribe()
    }

    if (this.detectLayerState$) {
      this.detectLayerState$.unsubscribe()
    }

    if (this.detectAllLayersState$) {
      this.detectAllLayersState$.unsubscribe()
    }
  }

  selectedDetectedLayer(id: any, layerId: any) {
    const layer = this.activeLayers.find((l) => {
      return l.get('id').value === layerId
    }) as FormGroup

    if (layer) {
      layer.get('feature').setValue(id)
    }
  }

  getSelectedFeatureById(fid: any, features: any[]) {
    const feature = features?.find(({ id }) => id === fid)

    let label = null

    if (feature) {
      label = feature.label
    }

    return label
  }

  detect(id: string) {
    const data = this.state.get('data').toJS()
    const pointFeature = new Feature({
      geometry: new Point([data.lng, data.lat]),
    })
    const json = new GeoJSON().writeGeometry(pointFeature.getGeometry())
    this.store.dispatch<any>(
      new DetectLayerRequest({
        id,
        geoJson: JSON.parse(json),
      }),
    )
  }

  featuresWithNone(features: any[]) {
    return [{ id: null, label: 'None' }, ...features]
  }

  detectAll() {
    const data = this.state.get('data').toJS()
    const pointFeature = new Feature({
      geometry: new Point([data.lng, data.lat]),
    })
    const json = new GeoJSON().writeGeometry(pointFeature.getGeometry())

    this.store.dispatch<any>(
      new DetectAllLayersRequest({
        geoJson: JSON.parse(json),
      }),
    )
  }

  get activeLayers() {
    return this.layers.get('rows')['controls'] as FormGroup[]
  }

  public onAdd(e: any, name: null | string = null) {
    const current = this.getField(['contactConnection', 'contact', name]) as any

    if (current) {
      e = [...current.toJS(), e.value]
    } else {
      e = [e.value]
    }

    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        `update.data.contactConnection.contact`,
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        `update.data`,
      ),
    )
    // this.cd.reattach()
  }

  public onRemove(e: any, name: null | string = null) {
    const current = this.getField(['contactConnection', 'contact', name]) as any

    var i = current.findIndex((item) => item === e)

    e = [...current.delete(i).toJS()]

    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        `update.data.contactConnection.contact`,
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        ' `update.data`',
      ),
    )
  }

  public compareFn(item: any, selected: any) {
    if (!!selected && isImmutable(selected)) {
      return item.id === selected.get('id')
    }
    return false
  }

  public handleFieldChange(
    e: any,
    name: null | string = null,
    path?: string,
  ): void {
    if (path) {
      const actualPath = ['data', ...path.split('.')]

      if (
        !this.state.getIn(actualPath) &&
        actualPath.includes('contactConnection')
      ) {
        this.store.dispatch(
          new UpdateField('contactConnection', { contact: {} }, `update.data`),
        )
      }
    }

    if (name === 'propertyId') {
      if (e && e.id) {
        this.store.dispatch(new FetchPropertyBuildingRequest(e.id))

        const property = this.properties.find(({ id }) => id === e?.id)

        if (property) {
          property.propertyCode = property.propertyCodeAssigned
          property.propertyId = property.id
          property.area = 0
          property.proportion = 0
          property.useProportion = false

          delete property.propertyCodeAssigned
          delete property.id
        } else {
          this.building = null
        }

        this.store.dispatch(
          new UpdateField('propertyConnection', property, `update.data`),
        )

        this.store.dispatch(
          new UpdateField('businessLayerFeatures', [], `update.data`),
        )
      } else {
        this.store.dispatch(
          new UpdateField('propertyConnection', null, `update.data`),
        )
        this.store.dispatch(new UpdateField('building', fromJS({}), `update`))
      }

      this.store.dispatch(
        new UpdateField('subcategoryConnection', null, `update.data`),
      )

      this.showLogicSlots = false
      this.subcategories = []
    } else if (name === 'categoryId') {
      const subcategoryConnection = this.state.getIn([
        'data',
        'subcategoryConnection',
      ])

      if (e == null) {
        this.clearSubcategoriesAndSlots()
      }

      if (subcategoryConnection === null) {
        this.store.dispatch(
          new UpdateField(
            'subcategoryConnection',
            {
              subCategory: {
                categoryId: e,
              },
            },
            `update.data`,
          ),
        )
      } else {
        this.store.dispatch(
          new UpdateField(
            'categoryId',
            e,
            `update.data.subcategoryConnection.subCategory`,
          ),
        )
        this.store.dispatch(
          new UpdateField(
            'id',
            null,
            `update.data.subcategoryConnection.subCategory`,
          ),
        )
      }
    } else if (name === 'logicSlot') {
      const logicSlot = this.logicSlots.find(({ id }) => e === id)

      this.store.dispatch(
        new UpdateField(
          'logicSlot',
          logicSlot,
          `update.data${path ? `.${path}` : ''}`,
        ),
      )
    } else {
      this.store.dispatch(
        new UpdateField(
          name || e.target.name,
          name
            ? e
            : isNaN(e.target.value) || e.target.value === ''
            ? e.target.value
            : parseFloat(e.target.value),
          `update.data${path ? `.${path}` : ''}`,
        ),
      )
    }

    if (name === 'subCategory' && this.propertyId) {
      if (this.isPropertyTypeInList(e.propertyType)) {
        this.store.dispatch(
          new UpdateField(
            'propertyType',
            e.propertyType,
            `update.data.propertyConnection`,
          ),
        )
      } else {
        this.store.dispatch(
          new UpdateField(
            'propertyType',
            null,
            `update.data.propertyConnection`,
          ),
        )
      }
    }

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state
          .get('validationErrors')
          .delete(
            path ? `${path}.${name || e.target.name}` : name || e.target.name,
          ),
        'update',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      name: 'required',
      'subcategoryConnection.subCategory.categoryId': 'required',
      lat: `min:${this.minLat}|max:${this.maxLat}|numeric`,
      lng: `min:${this.minLng}|max:${this.maxLng}|numeric`,
    }

    if (!!data.getIn(['contactConnection', 'contact'])) {
      rules['contactConnection.contact.firstName'] = 'required'
      rules['contactConnection.contact.lastName'] = 'required'
    }

    const selectedCategoryId = this.getField([
      'subcategoryConnection',
      'subCategory',
      'categoryId',
    ])
    const selectedSubCategoryId = this.getField([
      'subcategoryConnection',
      'subCategory',
      'id',
    ])

    const selectedCategory = this.categories.find(
      ({ id }) => id == selectedCategoryId,
    )

    // const selectedSubcategory = selectedCategory?.subCategories?.find(
    //   ({ id }) => id == selectedSubCategoryId,
    // )

    // const allowedSlotTypes =
    //   selectedSubcategory?.subCategoryType?.allowedSlotTypes

    if (selectedCategoryId) {
      if (selectedCategory?.subCategories?.length && !selectedSubCategoryId) {
        rules['subcategoryConnection.subCategory.id'] = 'required'
      }
    }

    // this.showLogicSlots =
    //           allowedSlotTypes?.includes('FIXED_MANUAL') &&
    //           !this.state.getIn(['data', 'propertyConnection']) &&
    //           this.logicSlotsOptions.length > 0

    if (this.showLogicSlots) {
      rules['subcategoryConnection.logicSlot'] = 'required'
    }

    if (this.propertyId) {
      rules['propertyConnection.propertyType'] = 'required'
    }

    const validator = new Validator(data.toJS(), rules)

    validator.setAttributeNames({
      'contactConnection.contact.firstName': 'first name',
      'contactConnection.contact.lastName': 'last name',
      'subcategoryConnection.logicSlot': 'type',
      'contactConnection.contact': 'contact',
      'subcategoryConnection.subCategory.categoryId': 'category',
      'subcategoryConnection.subCategory.id': 'subcategory',
      'propertyConnection.propertyType': 'property type',
      lat: 'latitude',
      lng: 'longitude',
    })

    return validator
  }

  public onSubmit(e: Event, options?: { validate?: boolean }): void {
    e.preventDefault()

    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch<any>(
        new UpdateField('validationErrors', {}, 'update'),
      )
      this.store.dispatch(new UpdateBusinessRequest(this.businessId, payload, options))
    } else {
      this.store.dispatch<any>(
        new UpdateField('validationErrors', validation.errors.errors, 'update'),
      )
    }
  }

  get propertyId() {
    return this.getField(['propertyConnection', 'propertyId'])
  }

  createNewContact(e: any) {
    this.contactConnectionValue = null
    this.store.dispatch(
      new UpdateField('contactConnection', { contact: {} }, `update.data`),
    )
  }

  handleEntityChange(entity: number) {
    if (entity == 2) {
      this.store.dispatch(
        new UpdateField('propertyConnection', null, `update.data`),
      )
      this.store.dispatch(new UpdateField('building', null, `update`))
      this.propertyEditable = false

      this.filterCategoriesAndSubCategories()
    }

    this.entityType = entity
  }

  isPropertyTypeInList(type: string) {
    return this.propertyTypes.includes(type)
  }

  public archive() {
    this.store.dispatch<any>(new ArchiveBusinessRequest(this.businessId))
  }

  public getRegistrantsNames(): string {
    const registrants = this.getField(['registrants'])
    if (registrants && registrants.size > 0) {
      return registrants.toJS().map((r: any) => r.name).join(', ')
    }
    return ''
  }
}
